<template>
  <!-- Checkbox & Radio - uses <fieldset class="form-fieldset"> & <legend class="form-fieldset__legend"> -->
  <fieldset
    v-if="type === 'checkbox' || type === 'radio'"
    class="form-fieldset"
    :class="{
      'has-error': !!error,
      'is-required': required,
      'has-warning': warning,
    }"
  >
    <slot name="label">
      <legend
        v-if="label"
        :for="name"
        class="form-fieldset__legend"
        :class="{'show-for-sr': !showLabel}"
        :qaid="qaid ? qaid + '__label' : null"
      >
        <span v-html="label"></span>
        <span v-if="required" class="text-red ml-25">
          *
          <span class="show-for-sr">(required)</span>
        </span>
      </legend>
    </slot>

    <div v-if="type === 'checkbox'">
      <!-- Checkbox -->
      <slot name="input">
        <label
          v-for="(multiItem, index) in multiItems"
          :key="index"
          class="checkbox"
          :class="labelClasses"
          :qaid="qaid"
        >
          <input
            :id="multiItem.value"
            ref="input"
            :aria-describedby="helpText ? helpId : null"
            :type="type"
            :name="multiItem.name || name"
            :value="multiItem.value"
            :disabled="disabled"
            :checked="multiItem.checked"
            :qaid="`formGroup${multiItem.value}`"
            @change="emitChange"
          />
          <span class="control-indicator"></span>
          <span v-html="multiItem.displayName"></span>
        </label>
      </slot>
    </div>

    <div v-else>
      <!-- Radio -->
      <slot name="input">
        <label v-for="(multiItem, index) in multiItems" :key="index" class="radio">
          <input
            :id="multiItem.value"
            ref="input"
            :aria-describedby="helpText ? helpId : null"
            :type="type"
            :name="name"
            :value="multiItem.value"
            :disabled="disabled"
            :checked="multiItem.value === value"
            :qaid="`formGroup${multiItem.value}`"
            @change="emitChange"
          />
          <span class="control-indicator"></span>
          <span :qaid="`formGroup${multiItem.value}_label`" v-html="multiItem.displayName"></span>
        </label>
      </slot>
    </div>

    <slot v-if="helpText" name="form-help">
      <span :id="helpId" class="form-help" v-html="helpText"></span>
    </slot>

    <slot v-if="showErrorMessages" name="form-errors">
      <span v-show="error" class="form-feedback is-invalid" v-html="error"></span>
    </slot>
  </fieldset>

  <!-- Input, Textarea & Select - uses <div class="form-group"> & <label class="form-label"> -->
  <div
    v-else
    class="form-group"
    :qaid="qaid"
    :class="{
      'has-error': canDisplayError,
      'is-required': required,
      'has-warning': warning,
    }"
  >
    <slot name="label">
      <label
        v-if="label"
        class="form-label"
        :for="name"
        :class="{'show-for-sr': !showLabel}"
        :qaid="qaid ? qaid + '__label' : null"
      >
        <span v-html="label"></span>
        <span v-if="required" class="text-red ml-25">
          *
          <span class="show-for-sr">(required)</span>
        </span>
      </label>
    </slot>

    <slot name="input">
      <!-- Select -->
      <div v-if="type === 'select'" class="select">
        <select
          :id="name"
          v-model="inputValue"
          :qaid="qaid"
          :name="name"
          class="form-control"
          :disabled="disabled"
          @input="emitChange"
        >
          <option
            v-for="(multiItem, index) in multiItems"
            :id="`${name}-${index}-${multiItem.value}`"
            ref="input"
            :key="`${name}-${index}-${multiItem.value}`"
            :type="type"
            :value="multiItem.value"
            :selected="value === multiItem.value"
            :aria-describedby="helpText ? helpId : null"
            :disabled="multiItem.disabled"
            v-html="multiItem.displayName"
          ></option>
        </select>
      </div>

      <!-- Textarea -->
      <textarea
        v-else-if="type === 'textArea' || type === 'textarea'"
        :id="name"
        ref="input"
        class="form-control"
        v-bind="$attrs"
        :aria-describedby="helpText ? helpId : null"
        :step="step"
        :type="type"
        :name="name"
        :value="value"
        :placeholder="placeholder"
        :rows="rows"
        :maxlength="maxlength"
        :disabled="disabled"
        @blur="emitBlur"
        @focus="emitFocus"
        @change="emitChange"
        @input="updateValue"
      ></textarea>

      <!-- Input -->
      <input
        v-else
        :id="name"
        ref="input"
        class="form-control"
        v-bind="$attrs"
        :aria-describedby="helpText ? helpId : null"
        :step="step"
        :type="type"
        :name="name"
        :value="value"
        :placeholder="placeholder"
        :maxlength="maxlength"
        :disabled="disabled"
        @keypress="checkInput"
        @blur="emitBlur"
        @focus="emitFocus"
        @change="emitChange"
        @input="updateValue"
      />
      <slot name="controls"></slot>
    </slot>

    <slot v-if="helpText && helpText !== error" name="form-help">
      <span :id="helpId" class="form-help" v-html="helpText"></span>
    </slot>

    <slot v-if="canDisplayError" name="form-errors">
      <span v-show="error" class="form-feedback is-invalid" v-html="error"></span>
    </slot>
  </div>
</template>

<script>
import first from 'lodash/first';

export default {
  name: 'FormGroup',
  inheritAttrs: false,
  inject: ['$validator'],

  $_veeValidate: {
    name() {
      return this.name;
    },
    value() {
      return this.$refs.input.value;
    },
  },

  props: {
    allowedCharactersRegex: {
      type: String,
      default: '',
    },
    autoCompleteOptions: {
      type: Array,
      default: () => [],
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    error: {
      type: String,
      required: false,
      default: '',
    },
    forceDisplayError: {
      type: Boolean,
      default: false,
    },
    helpText: {
      type: String,
      default: '',
    },
    label: {
      type: String,
      default: '',
    },
    labelClasses: {
      type: String,
      default: '',
    },
    maxlength: {
      type: Number,
      default: 250,
    },
    multiItems: {
      type: Array,
      default: () => [],
    },
    name: {
      type: String,
      required: true,
    },
    placeholder: {
      type: String,
      default: null,
    },
    qaid: {
      type: String,
      default: '',
    },
    required: {
      type: Boolean,
      default: false,
    },
    rows: {
      type: Number,
      default: 4,
    },
    showErrorMessages: Boolean,
    showLabel: {
      type: Boolean,
      default: true,
    },
    step: {
      type: String,
      default: null,
    },
    type: {
      type: String,
      default: 'text',
      validator: (val) => {
        return (
          [
            'url',
            'text',
            'password',
            'email',
            'search',
            'number',
            'tel',
            'textArea',
            'textarea',
            'select',
            'checkbox',
            'radio',
          ].indexOf(val) !== -1
        );
      },
    },
    validate: {
      type: String,
      default: '',
    },
    value: {
      type: null,
      default: null,
    },
    warning: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      inputValue: '',
    };
  },

  computed: {
    helpId() {
      return `${this.name}-help`;
    },
    handle() {
      return `${this.name}`;
    },
    canDisplayError() {
      return (
        this.error && this.showErrorMessages && (this.$validator.errors.items.length > 0 || this.forceDisplayError)
      );
    },
  },

  watch: {
    inputValue(value) {
      this.$emit('input', value);
    },
    value(value) {
      if (value !== this.inputValue) {
        this.inputValue = value;
      }
    },
    multiItems(items) {
      if (!items || !items.length || !this.inputValue) {
        return;
      }

      const hasItem = items.findIndex((item) => item.value === this.inputValue) > -1;
      if (!hasItem) {
        const itemsWithValues = items.filter((item) => item.value && item.value.length);
        if (itemsWithValues.length) {
          this.inputValue = first(itemsWithValues).value;
        }
      }
    },
    autoCompleteOptions() {
      if (this.autoCompleteOptions && this.autoCompleteOptions.length) this.enableAutoComplete();
    },
  },

  mounted() {
    if (this.$refs.input && this.$refs.input.value) {
      this.$refs.input.value = this.value;
    }

    if (this.value) {
      this.inputValue = this.value;
    }
  },

  methods: {
    checkInput(e) {
      if (this.allowedCharactersRegex) {
        const regex = RegExp(this.allowedCharactersRegex);
        if (regex.test(String.fromCharCode(e.charCode))) {
          return true;
        } else {
          e.preventDefault();
        }
      }
    },

    emitBlur(e) {
      this.$emit('blur', e);
    },

    emitFocus(e) {
      this.$emit('focus', e);
    },

    emitChange(e) {
      this.$emit('change', e);
    },

    enableAutoComplete() {
      // Extend jquery ui widget - http://api.jqueryui.com/1.11/autocomplete/#method-_renderItem
      $.widget('custom.formGroupAutocomplete', $.ui.autocomplete, {
        _renderItem: (ul, item) => {
          return $('<li class="py-1 px-1">')
            .attr('data-value', item.value)
            .append(item.label)
            .appendTo(ul);
        },
      });

      // Add autocomplete options
      const el = $(this.$refs.input);
      if (el)
        el.formGroupAutocomplete({
          source: this.autoCompleteOptions,
          change: (event, ui) => {
            if (ui && ui.item && ui.item.label) this.$emit('input', ui.item.label);
          },
          minLength: 3,
        });
    },

    updateValue(e) {
      this.$emit('input', e.target.value);
    },
  },
};
</script>
